import { AccordionContainer, Accordion, BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageHeaderRight, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { Component, JSX } from "react";
import { RouteComponentProps } from "react-router";
import { AccuracyTemplate, AccuracyTemplateService } from "../";
import { QESButton } from "@app/shared/qes-standards";
import { AccuracyTemplateInspection, AccuracyTemplateInspectionGroup } from "../accuracy/models";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";

interface AccuracyTemplateDetailsState {
  accuracyTemplateDetails: AccuracyTemplate;
  loading: boolean;
}

interface GroupResultMap {
  group: string;
  groupSort: number;
  result: string;
  resultSort: number;
}

type AccuracyTemplateTableData = {
  [wafflesKeyColumnName]: string;
  name: string;
  uid: JSX.Element;
} & { [key: string]: number | "N/A"; }

export default class AccuracyTemplateDetailsView extends Component<RouteComponentProps<{ templateCollectionId?: string, templateId?: string }>, AccuracyTemplateDetailsState> {

  private readonly templateService = new AccuracyTemplateService();

  constructor(props: RouteComponentProps<{ templateCollectionId?: string, templateId?: string }>) {
    super(props);

    this.state = {
      accuracyTemplateDetails: null,
      loading: true,
    };
  }

  public async componentDidMount() {
    const templateCollectionId = this.props.match.params.templateCollectionId;
    const templateId = this.props.match.params.templateId;

    if (templateCollectionId && templateId) {
      const templateDetails =
        await this.templateService.getTemplateDetails(templateCollectionId, templateId);

      this.setState({
        accuracyTemplateDetails: templateDetails,
        loading: false,
      });
    }
  }

  public render() {
    const hasResults = !this.state.loading && this.state.accuracyTemplateDetails != null;

    const title = this.state.loading ?
      "Loading Template..." :
      hasResults ?
        `${this.state.accuracyTemplateDetails.name}` :
        "Template Not Found";

    if (!hasResults) {
      return null;
    }

    return (
      <Page documentTitle="Accuracy Template Details">
        <Breadcrumbs template={BreadcrumbTemplateEnum.NetworkAssessment} items={["Accuracy Template"]} />
        <PageInner>
          <PageHeader title={title}>
            <PageHeaderRight>
              <QESButton qesTestId="accuracy-template-details-view_go-back" qesType="secondary" onClick={this.handleCancel}>Go Back</QESButton>
            </PageHeaderRight>
          </PageHeader>

          <PageSection>
            <div className="container-fluid">
              {hasResults &&
                <>
                  <hr className="mt0 mb30" />
                  {
                    this.renderAccuracyTemplateDetails()
                  }
                </>
              }
            </div>
          </PageSection>
        </PageInner>
      </Page>);
  }

  private handleCancel = () => {
    this.props.history.goBack();
  }

  private renderAccuracyTemplateDetails() {
    const groupResultMaps = this.getGroupResultMaps();
    return (
      <>
        <AccordionContainer>
          <Accordion title="Details" defaultOpen={true} testId="accuracy-template-details-view__details-accordion">
            <div className="row card-body">
              <div className="col-md-6"><h4 className="card-title">Scope</h4>
                <div className="card-body">
                  <dt>Query Type:</dt> <dd>{this.state.accuracyTemplateDetails.scope.queryType}</dd>
                  <dt>Only Truth Source Matched:</dt> <dd>{this.state.accuracyTemplateDetails.scope.onlyTruthSourceMatched ? "Yes" : "No"}</dd>
                  <dt>Sample Size:</dt> <dd>{(this.state.accuracyTemplateDetails.scope.sampling && this.state.accuracyTemplateDetails.scope.sampling.sampleSize) ? this.state.accuracyTemplateDetails.scope.sampling.sampleSize : "N/A"}</dd>
                  <dt>Iterations:</dt> <dd>{(this.state.accuracyTemplateDetails.scope.sampling && this.state.accuracyTemplateDetails.scope.sampling.iterations) ? this.state.accuracyTemplateDetails.scope.sampling.iterations : "N/A"}</dd>
                </div>
              </div>
              <div className="col-md-6"><h4 className="card-title">Scoring</h4>
                <div className="card-body">
                  <dt>Max Points:</dt> <dd>{this.state.accuracyTemplateDetails.scoring.maxPoints}</dd>
                  <dt>Point Style:</dt> <dd>{this.state.accuracyTemplateDetails.scoring.pointStyle}</dd>
                  <dt>Row Score Method:</dt> <dd>{this.state.accuracyTemplateDetails.scoring.rowScoreMethod}</dd>
                  <dt>Aggregate Score Method:</dt> <dd>{this.state.accuracyTemplateDetails.scoring.aggregateScoreMethod}</dd>
                </div>
              </div>
            </div>
          </Accordion>
        </AccordionContainer>
        {this.state.accuracyTemplateDetails && this.state.accuracyTemplateDetails.criteria.inspectionGroups.map((inspectionGroup) => {
          return (
            <AccordionContainer key={inspectionGroup.name}>
              <Accordion title={inspectionGroup.name} defaultOpen={true} testId={`accuracy-template-details-view__${inspectionGroup.name}-accordion`}>
                <Waffles<AccuracyTemplateTableData>
                  columns={this.getColumnConfig(groupResultMaps)}
                  data={this.getTableData(inspectionGroup, groupResultMaps)}
                  fullWidth={true}
                  testId={`accuracy-template-details-${inspectionGroup.name.replaceAll(" ", "-").toLowerCase()}`}
                />
              </Accordion>
            </AccordionContainer>
          );
        },
        )}
      </>
    );
  }

  private getGroupResultMaps = () => {
    const groupResultMaps: GroupResultMap[] = [];
    for (const group of this.state.accuracyTemplateDetails.criteria.inspectionGroups) {
      for (const inspection of group.inspections) {
        for (const result of inspection.results) {
          const groupSort = this.state.accuracyTemplateDetails.criteria.inspectionGroups.indexOf(group);
          const resultSort = (result.result === null || result.result === undefined) ? 999 : groupResultMaps.length;
          const groupResultMap = { group: group.name, groupSort, result: result.result, resultSort };
          if (groupResultMaps.findIndex((m) => m.group === group.name && m.result === result.result) < 0) {
            groupResultMaps.push(groupResultMap);
          }
        }
      }
    }

    return groupResultMaps
      .sort((a, b) =>
        a.groupSort < b.groupSort ? -1 :
          a.groupSort > b.groupSort ? 1 :
            a.resultSort < b.resultSort ? -1 :
              a.resultSort > b.resultSort ? 1 : 0
      );
  }

  private getTableData = (inspectionGroup: AccuracyTemplateInspectionGroup, groupResultMaps: GroupResultMap[]) => {
    /* eslint-disable sort-keys */
    const tableData = inspectionGroup.inspections.map((inspection) => {
      // If I type data as AccuracyTemplateTableData it complains that its properties don't meet the 
      // [key: string]: number | "N/A" constraint of the result groups
      const data = {
        [wafflesKeyColumnName]: inspection.uid,
        uid: (
          <>
            <TooltipWrapper text={inspection.uid}>
              <span className="fa fa-info-circle" aria-label={inspection.uid} />
            </TooltipWrapper>
          </>
        ),
        name: inspection.name,
      };

      groupResultMaps.filter((grms) => grms.group === inspectionGroup.name).forEach((grm) => {
        (data as AccuracyTemplateTableData)[grm.result] = this.GetResult(grm, inspection);
      });

      return data;
    });
    /* eslint-enable */

    return tableData as AccuracyTemplateTableData[];
  }

  private getColumnConfig = (groupResultMaps: GroupResultMap[]) => {
    const config: ColumnConfig = {
      "name": {
        displayText: "Name",
        noSort: true,
        testId: "name",
      },
      "uid": {
        displayText: "UID",
        noSort: true,
        testId: "uid",
      },
    };

    groupResultMaps.forEach((grm) => {
      if (grm.result !== undefined) {
        config[grm.result] = {
          displayText: `${grm.result} Result`,
          noSort: true,
          testId: grm.result.replaceAll(" ", "-").toLowerCase(),
        }
      }
    });

    return config;
  }

  private GetResult(grm: GroupResultMap, inspection: AccuracyTemplateInspection) {
    const result = inspection.results.find((r) => r.result === grm.result);
    return result != null ? result.points : "N/A";
  }
}
